import FormDropdown, {
    FormDropdownOption,
    buildOptionsWithSections,
} from "../FormDropdown";
import SectionTitle from "../SectionTitle";
import {
    useAvailableAuditPeriods,
    useAuditReadiness,
} from "../../api/audit-readiness";
import { useCallback, useEffect, useState } from "react";
import { AvailablePeriod } from "../../api/models/AuditReadiness";
import ISO14064Spider from "./ISO14064Spider";
import ToggleButton from "../ToggleButton";
import Typography from "../Typography";
import clsx from "clsx";

const ComplianceSection = () => {
    const [comparisonPeriodEnabled, setComparisonPeriodEnabled] =
        useState(false);

    const [periodIndex, setPeriodIndex] = useState<number>(-1);
    const [comparisonPeriodIndex, setComparisonPeriodIndex] =
        useState<number>(-1);

    const { availablePeriods, isLoading: isLoadingAvailablePeriods } =
        useAvailableAuditPeriods();

    const { auditReadinessData } = useAuditReadiness(
        availablePeriods[periodIndex]?.year,
        availablePeriods[periodIndex]?.quarter,
        availablePeriods[comparisonPeriodIndex]?.year,
        availablePeriods[comparisonPeriodIndex]?.quarter
    );

    const onPeriodChange = useCallback((newPeriodIndex: number) => {
        // If user selects the same period for period 1 and 2, reset period 2
        if (newPeriodIndex === comparisonPeriodIndex) {
            setComparisonPeriodIndex(-1);
        }

        setPeriodIndex(newPeriodIndex);
    }, [comparisonPeriodIndex]);

    useEffect(() => {
        if (availablePeriods.length === 0) {
            setPeriodIndex(-1);
            return;
        }

        if (periodIndex === -1) {
            onPeriodChange(0);
            return;
        }
    }, [availablePeriods.length, onPeriodChange, periodIndex]);

    const periodOptions: FormDropdownOption<number>[] = availablePeriods
        ? availablePeriods.map((period: AvailablePeriod, index: number) => ({
              value: index,
              label: `${period.year} - Q${period.quarter}`,
          }))
        : [];

    const periodOptionsWithSections = (
        options: FormDropdownOption<number>[]
    ) => {
        return buildOptionsWithSections(options, (option) => {
            return {
                sectionTitle: availablePeriods[option.value].year.toString(),
                sectionOrder: -availablePeriods[option.value].year,
            };
        });
    };



    const onToggleComparison = () => {
        if (comparisonPeriodEnabled) {
            setComparisonPeriodIndex(-1);
            setComparisonPeriodEnabled(false);

            return;
        }

        if (periodIndex === availablePeriods.length - 1) {
            setComparisonPeriodIndex(-1);
        } else {
            setComparisonPeriodIndex(periodIndex + 1);
        }

        setComparisonPeriodEnabled(true);
    };

    type SelectedPeriodWrapperProps = {
        selectedText?: string;
        colorClass: string;
    };

    const SelectedPeriodWrapper = ({
        selectedText: children,
        colorClass,
    }: SelectedPeriodWrapperProps) => {
        return (
            <>
                <div
                    className={clsx(
                        "h-3 w-3 ml-1 mr-2 rounded-full outline-double outline-1 opacity-20",
                        colorClass
                    )}
                />
                {children}
            </>
        );
    };

    return (
        <>
            <SectionTitle title="Audit Readiness" />
            <div className="flex justify-between items-center bg-surface-light-1 rounded-xl my-5 p-6 pb-8">
                <span className="flex flex-wrap gap-6 items-center">
                    <div>
                        <FormDropdown
                            label="Select framework"
                            options={[
                                {
                                    value: "iso14064",
                                    label: "ISO 14064",
                                },
                            ]}
                            value={"iso14064"}
                            onChange={() => null}
                            className="w-56"
                        />
                        {!isLoadingAvailablePeriods &&
                            availablePeriods.length === 0 && (
                                <Typography
                                    className="mt-2"
                                    variant="label-medium"
                                    component="p"
                                >
                                    No audit data available for this framework.
                                </Typography>
                            )}
                    </div>
                    <FormDropdown
                        label="Period"
                        options={periodOptionsWithSections(periodOptions)}
                        value={periodIndex}
                        onChange={onPeriodChange}
                        isLoadingOptions={isLoadingAvailablePeriods}
                        className="w-56"
                        selectedTextWrapper={(selectedText: string) => (
                            <SelectedPeriodWrapper
                                selectedText={selectedText}
                                colorClass="bg-[#54D7F2]"
                            />
                        )}
                    />
                    {comparisonPeriodEnabled && (
                        <FormDropdown
                            label="Comparison period"
                            options={periodOptionsWithSections(
                                periodOptions.filter(
                                    (period) =>
                                        period !== periodOptions[periodIndex]
                                )
                            )}
                            value={comparisonPeriodIndex}
                            onChange={setComparisonPeriodIndex}
                            isLoadingOptions={isLoadingAvailablePeriods}
                            className="w-56"
                            selectedTextWrapper={(selectedText: string) => (
                                <SelectedPeriodWrapper
                                    selectedText={selectedText}
                                    colorClass="bg-[#8EB85B]"
                                />
                            )}
                        />
                    )}
                </span>
                {availablePeriods.length > 1 && (
                    <ToggleButton
                        label="Comparison"
                        className="pt-6 font-bold "
                        onChange={onToggleComparison}
                        disabled={
                            isLoadingAvailablePeriods ||
                            availablePeriods.length <= 1 ||
                            periodIndex < 0
                        }
                    />
                )}
            </div>
            <div>
                {auditReadinessData && !isLoadingAvailablePeriods && (
                    <ISO14064Spider auditReadinessData={auditReadinessData} />
                )}
            </div>
        </>
    );
};

export default ComplianceSection;
